import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A product-page block: price + add-to-cart button that morphs into an inline
 * quantity stepper. UI updates are optimistic (instant), while cart API calls
 * are serialized so rapid taps always resolve to the latest intended quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} stepper
 * @property {HTMLElement} countDisplay
 * @property {HTMLButtonElement} minusBtn
 * @property {HTMLButtonElement} plusBtn
 *
 * @extends {Component<Refs>}
 */
class DynamicAtcComponent extends Component {
  /** Last quantity confirmed by the server. */
  #qty = 0;

  /** Optimistically displayed quantity (may differ while API is in flight). */
  #dispQty = 0;

  /** Latest quantity requested by the user. */
  #targetQty = 0;

  /** Whether a cart update request is currently in flight. */
  #requestInFlight = false;

  /** Incremented to invalidate in-flight responses after external cart syncs. */
  #requestVersion = 0;

  /** Preserves "add" semantics if the user adds then taps + before the request finishes. */
  #pendingWasAdd = false;

  /** Last cart item count shown/published globally. */
  #lastKnownItemCount = null;

  /** Variant quantity already reflected by delayed header counter updates. */
  #publishedVariantQty = 0;

  /** Used to cancel delayed counter ticks after cart sync/errors. */
  #counterQueueVersion = 0;

  /** Delayed positive counter ticks waiting for fly-to-cart landing. */
  #pendingCounterDeltas = [];

  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {ResizeObserver | null} */
  #stickyResizeObserver = null;

  /** @type {number | null} */
  #stickyVisibilityFrame = null;

  /** @type {Element | null} */
  #stickyTrigger = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {AbortController | null} */
  #stickyButtonAbortController = null;

  static get observedAttributes() {
    return ['data-variant-id', 'data-cart-quantity', 'data-product-variant-media', 'data-available'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#qty = this.#readCartQty();
    this.#dispQty = this.#qty;
    this.#targetQty = this.#qty;
    this.#publishedVariantQty = this.#qty;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal,
    });

    // data-state is set correctly by Liquid on initial render.
    this.#syncAvailability();
    this.#syncCount();
    this.#setupStickyObserver();
    this.#bindStickyButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#stickyButtonAbortController?.abort();
    this.#stickyButtonAbortController = null;
    this.#buyButtonsObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#stickyResizeObserver?.disconnect();
    window.removeEventListener('scroll', this.#scheduleStickyVisibilityUpdate);
    window.removeEventListener('resize', this.#scheduleStickyVisibilityUpdate);

    if (this.#stickyVisibilityFrame) {
      cancelAnimationFrame(this.#stickyVisibilityFrame);
    }
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (!this.isConnected || oldVal === newVal) return;

    if (name === 'data-cart-quantity') {
      this.#qty = parseInt(newVal ?? '0') || 0;
      this.#dispQty = this.#qty;
      this.#targetQty = this.#qty;
      this.#publishedVariantQty = this.#qty;
    }

    if (name === 'data-variant-id' || name === 'data-cart-quantity') {
      this.#requestVersion++;
      this.#counterQueueVersion++;
      this.#pendingCounterDeltas = [];
      this.#requestInFlight = false;
      this.#pendingWasAdd = false;
      this.#applyState(this.#qty > 0 ? 'stepper' : 'add', false);
    }

    if (name === 'data-available') {
      this.#syncAvailability();
      this.#scheduleStickyVisibilityUpdate();
    }
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  /** First click: show stepper immediately then add to cart. */
  handleAdd(event, sourceButton) {
    if (!this.#isAvailable()) return;
    if (this.#dispQty > 0) return;
    const { addButton } = this.refs;
    if (!addButton || addButton.disabled) return;

    this.#setTargetQuantity(1, true, this.#flyToCartWhenEnabled(this.#getFlySource(event, sourceButton)));
  }

  /**
   * Increment: updates display immediately and queues the latest intended
   * quantity while any previous request is still in flight.
   */
  handlePlus(event, sourceButton) {
    if (!this.#isAvailable()) return;
    if (this.#dispQty <= 0) return;

    this.#setTargetQuantity(
      this.#dispQty + 1,
      true,
      this.#flyToCartWhenEnabled(this.#getFlySource(event, sourceButton))
    );
  }

  /**
   * Decrement: updates display immediately. At 1 → 0 the button reverts
   * instantly while the serialized API queue catches up.
   */
  handleMinus() {
    if (!this.#isAvailable()) return;
    if (this.#dispQty <= 0) return;

    this.#setTargetQuantity(this.#dispQty - 1, true);
  }

  /**
   * Public API for external components (e.g., the Quantity Breaks tier
   * picker) to set the cart-line quantity to an exact value. Reuses the
   * same optimistic-display + serialized-cart-commit + optimistic-bubble
   * pipeline as the +/- buttons.
   *
   * @param {number} qty - Target quantity. Negative values are clamped to 0.
   */
  setQuantity(qty) {
    if (!this.#isAvailable()) return;
    const target = Math.max(0, Math.floor(Number(qty) || 0));
    if (target === this.#dispQty) return;
    this.#setTargetQuantity(target, true);
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  /**
   * Applies the user's intended quantity immediately, publishes an optimistic
   * cart count update, then queues a serialized cart update request.
   * @param {number} quantity
   * @param {boolean} animate
   * @param {Promise<void>} [counterDelay]
   */
  #setTargetQuantity(quantity, animate, counterDelay = Promise.resolve()) {
    const nextQty = Math.max(0, quantity);
    const previousQty = this.#dispQty;
    if (nextQty === previousQty) return;

    if (this.#qty === 0 && this.#targetQty === 0 && nextQty > 0) {
      this.#pendingWasAdd = true;
    }

    if (nextQty === 0) {
      this.#pendingWasAdd = false;
    }

    this.#dispQty = nextQty;
    this.#targetQty = nextQty;

    if (nextQty > 0) {
      this.#applyState('stepper', previousQty <= 0 ? animate : false);
    } else {
      this.#applyState('add', animate);
    }

    this.#queueCartCountDelta(nextQty - previousQty, counterDelay);
    this.#flushCartUpdate();
  }

  async #flushCartUpdate() {
    if (this.#requestInFlight || this.#targetQty === this.#qty) return;

    const targetQty = this.#targetQty;
    const version = this.#requestVersion;
    const shouldDispatchAsAdd = this.#pendingWasAdd && targetQty > 0;

    this.#requestInFlight = true;

    try {
      const cart = await this.#updateCartQuantity(targetQty);
      if (version !== this.#requestVersion) return;

      // Use the quantity the server actually confirmed (may be lower due to inventory limits).
      const confirmedQty =
        cart.items?.find((item) => item.variant_id?.toString() === this.dataset.variantId)
          ?.quantity ?? targetQty;

      this.#qty = confirmedQty;

      const serverCapped = confirmedQty < targetQty;
      if (serverCapped) {
        this.#dispQty = confirmedQty;
        this.#targetQty = confirmedQty;
        this.#syncCount();
      }

      if (this.#targetQty === targetQty || serverCapped) {
        this.#pendingWasAdd = false;
        this.#dispatchCartEvent(cart, shouldDispatchAsAdd);

        // Background correction — deliberately not awaited, so the finally block below
        // still runs promptly. Only on the settling request: if more clicks are queued,
        // #flushCartUpdate issues another request that reconciles at its own end.
        this.#reconcileCartCount(cart, version).catch(() => {});
      }
    } catch {
      if (version !== this.#requestVersion) return;

      // Revert display to last server-confirmed quantity.
      this.#dispQty = this.#qty;
      this.#targetQty = this.#qty;
      this.#pendingWasAdd = false;
      this.#cancelPendingCartCountDeltas();

      if (this.#qty <= 0) {
        this.#applyState('add', false);
      } else {
        this.#applyState('stepper', false);
      }

      this.#syncPublishedVariantQuantity(this.#qty);
    } finally {
      if (version !== this.#requestVersion) return;

      this.#requestInFlight = false;
      if (this.#targetQty !== this.#qty) {
        this.#flushCartUpdate();
      }
    }
  }

  /**
   * @param {number} targetQty
   */
  async #updateCartQuantity(targetQty) {
    const variantId = this.dataset.variantId;
    if (!variantId) throw new Error('Variant ID is missing');

    const sections = this.#cartSectionIds();
    const res = await fetch(
      Theme.routes.cart_update_url,
      {
        ...fetchConfig('json', {
          body: JSON.stringify({
            updates: { [variantId]: targetQty },
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        }),
        credentials: 'same-origin',
      }
    );

    if (!res.ok) throw new Error('Cart update failed');

    const cart = await res.json();
    if (cart.status) throw new Error(cart.message || 'Cart update failed');

    return cart;
  }

  /**
   * @param {Object} cart
   * @param {boolean} shouldDispatchAsAdd
   */
  #dispatchCartEvent(cart, shouldDispatchAsAdd) {
    const EventClass = shouldDispatchAsAdd ? CartAddEvent : CartUpdateEvent;

    this.dispatchEvent(
      new EventClass(cart, this.id, {
        source: 'dynamic-atc',
        variantId: this.dataset.variantId,
        itemCount: cart.item_count,
        deferCartBubble: true,
        sections: cart.sections,
      })
    );
  }

  /**
   * Queues header counter updates so add/increment ticks land with fly-to-cart.
   * @param {number} delta
   * @param {Promise<void>} counterDelay
   */
  #queueCartCountDelta(delta, counterDelay) {
    if (delta === 0) return;

    if (delta < 0 && this.#cancelPendingPositiveDelta()) {
      return;
    }

    if (delta < 0) {
      this.#publishOptimisticCartUpdate(delta);
      return;
    }

    // Reserve the resulting total now, at click time, and publish that fixed number when
    // the tick fires — do not recompute from the DOM later.
    //
    // #cartSectionIds() returns the section hosting cart-items-component, which on a
    // product page is the header, and the header renders cart-bubble. So the cart response
    // morphs the bubble to the server's item_count, and that lands well before the
    // fly-to-cart animation ends. Recomputing at fire time would read that already-updated
    // total and add this delta on top of itself — the bubble ticking 1 -> 2 -> 3 for a
    // single click on a cart that holds 2.
    //
    // Reserving also keeps consecutive clicks ordered: each queue reads the previous
    // reservation, so two quick clicks resolve to 2 then 3 rather than both to 2.
    const base = this.#lastKnownItemCount ?? this.#readCartItemCount();
    const reserved = Math.max(0, base + delta);

    const token = {
      canceled: false,
      delta,
      itemCount: reserved,
      version: this.#counterQueueVersion,
    };

    this.#lastKnownItemCount = reserved;
    this.#publishedVariantQty = Math.max(0, this.#publishedVariantQty + delta);

    this.#pendingCounterDeltas.push(token);

    // Keep the chained promise on the token, not `counterDelay` itself, so awaiting it
    // guarantees this handler has already run — #reconcileCartCount relies on that.
    token.settled = counterDelay.finally(() => {
      this.#pendingCounterDeltas = this.#pendingCounterDeltas.filter((pendingToken) => pendingToken !== token);

      if (token.canceled || token.version !== this.#counterQueueVersion) return;

      this.#publishCartCount(token.itemCount);
    });
  }

  /**
   * Resolves once every queued counter tick has fired or been dropped.
   * Loops because a tick can be queued while we are waiting on the current batch.
   */
  async #drainPendingCounterDeltas() {
    for (let pass = 0; pass < 10 && this.#pendingCounterDeltas.length; pass++) {
      await Promise.allSettled(this.#pendingCounterDeltas.map((token) => token.settled));
    }
  }

  /**
   * Snaps the header count back to the cart the server actually returned.
   *
   * The bubble is driven purely by optimistic deltas — #dispatchCartEvent sets
   * deferCartBubble so cart-icon ignores the authoritative count and the tick can stay in
   * step with the fly-to-cart animation. That leaves no path back to the truth, so a
   * dropped, duplicated or inventory-capped tick sticks until the next full page load.
   *
   * Must wait for the queue to drain: positive ticks are held behind the animation delay,
   * and #publishedVariantQty does not count a token that has not fired yet, so correcting
   * first would let that tick re-add the same unit afterwards.
   *
   * @param {Object} cart - The cart returned by the settling request.
   * @param {number} version - The request version this reconciliation belongs to.
   */
  async #reconcileCartCount(cart, version) {
    const serverCount = cart?.item_count;
    if (typeof serverCount !== 'number') return;

    await this.#drainPendingCounterDeltas();

    // A newer interaction started while we waited; it will reconcile itself.
    if (version !== this.#requestVersion) return;

    const currentCount = this.#lastKnownItemCount ?? this.#readCartItemCount();
    if (currentCount !== serverCount) {
      this.#publishOptimisticCartUpdate(serverCount - currentCount);
    }

    // Re-anchor the per-variant tally to the confirmed quantity so the next delta is
    // measured from the truth rather than from whatever the ticks accumulated.
    this.#publishedVariantQty = this.#qty;
  }

  /**
   * Updates the header cart count without forcing cart sections to re-render.
   * @param {number} delta
   */
  #publishOptimisticCartUpdate(delta) {
    if (delta === 0) return;

    const baseItemCount = this.#lastKnownItemCount ?? this.#readCartItemCount();
    const itemCount = Math.max(0, baseItemCount + delta);
    this.#lastKnownItemCount = itemCount;
    this.#publishedVariantQty = Math.max(0, this.#publishedVariantQty + delta);

    this.#publishCartCount(itemCount);
  }

  /**
   * Announces an absolute header count. Only cart-icon acts on it — both this component
   * and cart-items-component skip events flagged optimistic.
   * @param {number} itemCount
   */
  #publishCartCount(itemCount) {
    this.dispatchEvent(
      new CartUpdateEvent({ item_count: itemCount }, this.id, {
        source: 'dynamic-atc',
        variantId: this.dataset.variantId,
        itemCount,
        variantQuantity: this.#dispQty,
        optimistic: true,
      })
    );
  }

  #cancelPendingPositiveDelta() {
    const token = [...this.#pendingCounterDeltas]
      .reverse()
      .find((pendingToken) => !pendingToken.canceled && pendingToken.delta > 0);

    if (!token) return false;

    token.canceled = true;

    // The reservation was taken when the tick was queued but never published, so release
    // it here — otherwise the minus that cancelled this plus leaves the header one high.
    this.#lastKnownItemCount = Math.max(0, (this.#lastKnownItemCount ?? 0) - token.delta);
    this.#publishedVariantQty = Math.max(0, this.#publishedVariantQty - token.delta);

    return true;
  }

  #cancelPendingCartCountDeltas() {
    this.#counterQueueVersion++;
    this.#pendingCounterDeltas.forEach((token) => {
      token.canceled = true;
    });
    this.#pendingCounterDeltas = [];
  }

  /**
   * Corrects the header count if published delayed ticks differ from the final
   * server-confirmed quantity for this variant.
   * @param {number} quantity
   */
  #syncPublishedVariantQuantity(quantity) {
    const correctionDelta = quantity - this.#publishedVariantQty;
    this.#publishOptimisticCartUpdate(correctionDelta);
    this.#publishedVariantQty = quantity;
  }

  /**
   * Keeps this block synchronized when the cart changes elsewhere, including
   * the drawer's clear-all button.
   * @param {CartAddEvent | CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.sourceId === this.id || event.detail?.data?.optimistic) return;
    if (this.#requestInFlight) return;

    const cart = event.detail?.resource;
    if (!cart?.items) return;
    if (typeof cart.item_count === 'number') {
      this.#lastKnownItemCount = cart.item_count;
    }

    const cartItem = cart.items.find((item) => item.variant_id?.toString() === this.dataset.variantId);
    const cartQty = cartItem ? cartItem.quantity : 0;

    this.#requestVersion++;
    this.#cancelPendingCartCountDeltas();
    this.#requestInFlight = false;
    this.#pendingWasAdd = false;
    this.#qty = cartQty;
    this.#dispQty = cartQty;
    this.#targetQty = cartQty;
    this.#publishedVariantQty = cartQty;
    this.#applyState(cartQty > 0 ? 'stepper' : 'add', false);
  };

  /**
   * Keeps the custom ATC block in sync with the variant picker's section HTML.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const newDynamicAtc = event.detail.data.html.querySelector(
      `dynamic-atc-component[data-product-id="${this.dataset.productId}"]`
    );
    if (!(newDynamicAtc instanceof HTMLElement)) return;

    const stickyWasStuck = this.refs.stickyBar?.getAttribute('data-stuck') ?? 'false';

    this.#requestVersion++;
    this.#cancelPendingCartCountDeltas();
    this.#requestInFlight = false;
    this.#pendingWasAdd = false;

    morph(this, newDynamicAtc, {
      ...MORPH_OPTIONS,
      childrenOnly: false,
      onAfterUpdate: (node) => {
        if (node !== this) MORPH_OPTIONS.onAfterUpdate?.(node);
      },
    });
    this.updatedCallback();

    this.#qty = this.#readCartQty();
    this.#dispQty = this.#qty;
    this.#targetQty = this.#qty;
    this.#publishedVariantQty = this.#qty;

    if (event.detail.resource == null) {
      this.dataset.available = 'false';
      this.dataset.variantId = '';
      this.dataset.cartQuantity = '0';
      this.#qty = 0;
      this.#dispQty = 0;
      this.#targetQty = 0;
      this.#publishedVariantQty = 0;
    }

    if (this.refs.stickyBar) {
      this.refs.stickyBar.dataset.stuck = stickyWasStuck;
      if (stickyWasStuck === 'true') {
        this.refs.stickyBar.removeAttribute('aria-hidden');
      } else {
        this.refs.stickyBar.setAttribute('aria-hidden', 'true');
      }
    }

    this.#applyState(this.#qty > 0 ? 'stepper' : 'add', false);
    this.#bindStickyButtons();
    this.#scheduleStickyVisibilityUpdate();
  };

  /**
   * Launches the same fly-to-cart image animation used by the standard and
   * PTC add-to-cart buttons.
   */
  #flyToCartWhenEnabled(sourceEl) {
    if (this.dataset.addToCartAnimation !== 'true') {
      return Promise.resolve();
    }

    return this.#animateFlyToCart(sourceEl);
  }

  #animateFlyToCart(sourceEl) {
    const { addButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;
    const source = sourceEl ?? addButton;

    if (!cartIcon || !source || !image) return Promise.resolve();

    return new Promise((resolve) => {
      const launch = () => {
        /** @type {any} */
        const flyEl = document.createElement('fly-to-cart');
        flyEl.classList.add('fly-to-cart--main');
        flyEl.style.setProperty('background-image', `url(${image})`);
        flyEl.style.setProperty('--start-opacity', '0');
        flyEl.source = source;
        flyEl.destination = cartIcon;

        let resolved = false;
        const finish = () => {
          if (resolved) return;
          resolved = true;
          resolve();
        };

        flyEl.addEventListener('fly-to-cart:done', finish, { once: true });
        document.body.appendChild(flyEl);
        setTimeout(finish, 3200);
      };

      if (customElements.get('fly-to-cart')) {
        launch();
      } else {
        customElements.whenDefined('fly-to-cart').then(launch);
      }
    });
  }

  /**
   * @param {'add' | 'stepper'} state
   * @param {boolean} animate
   */
  #applyState(state, animate) {
    const isAvailable = this.#isAvailable();

    if (!animate) {
      this.classList.add('dynamic-atc--no-transition');
    }

    this.dataset.state = state;
    const { stickyBar } = this.refs;
    if (stickyBar) {
      stickyBar.dataset.state = state;
      stickyBar.dataset.available = String(isAvailable);
    }
    this.#syncCount();

    if (state === 'add') {
      if (this.refs.addButton) this.refs.addButton.disabled = !isAvailable;
      if (this.refs.stickyAddButton) this.refs.stickyAddButton.disabled = !isAvailable;
    }

    if (!isAvailable) {
      if (this.refs.addButton) this.refs.addButton.disabled = true;
      if (this.refs.stickyAddButton) this.refs.stickyAddButton.disabled = true;
    }

    if (!animate) {
      requestAnimationFrame(() => this.classList.remove('dynamic-atc--no-transition'));
    }
  }

  #syncCount() {
    const text = String(this.#dispQty > 0 ? this.#dispQty : 1);
    const { countDisplay, stickyCountDisplay } = this.refs;
    if (countDisplay) countDisplay.textContent = text;
    if (stickyCountDisplay) stickyCountDisplay.textContent = text;
  }

  #syncAvailability() {
    const isAvailable = this.#isAvailable();
    const { addButton, stickyAddButton, stickyBar } = this.refs;

    if (stickyBar) stickyBar.dataset.available = String(isAvailable);

    if (!isAvailable) {
      if (addButton) addButton.disabled = true;
      if (stickyAddButton) stickyAddButton.disabled = true;
      return;
    }

    if (this.dataset.state === 'add') {
      if (addButton) addButton.disabled = false;
      if (stickyAddButton) stickyAddButton.disabled = false;
    }
  }

  #isAvailable() {
    return this.dataset.available !== 'false';
  }

  #readCartQty() {
    return parseInt(this.dataset.cartQuantity ?? '0') || 0;
  }

  // ─── Sticky bar ───────────────────────────────────────────────────────────

  #setupStickyObserver() {
    const stickyTrigger = this.#getStickyTrigger();
    if (!stickyTrigger) return;

    this.#stickyTrigger = stickyTrigger;

    this.#buyButtonsObserver = new IntersectionObserver(this.#scheduleStickyVisibilityUpdate);

    this.#buyButtonsObserver.observe(stickyTrigger);

    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (footer) {
      this.#footerObserver = new IntersectionObserver(
        this.#scheduleStickyVisibilityUpdate,
        { rootMargin: '200px 0px 0px 0px' }
      );
      this.#footerObserver.observe(footer);
    }

    this.#stickyResizeObserver = new ResizeObserver(this.#scheduleStickyVisibilityUpdate);
    this.#stickyResizeObserver.observe(this);
    this.#stickyResizeObserver.observe(stickyTrigger);
    if (this.refs.stickyBar) this.#stickyResizeObserver.observe(this.refs.stickyBar);

    window.addEventListener('scroll', this.#scheduleStickyVisibilityUpdate, { passive: true });
    window.addEventListener('resize', this.#scheduleStickyVisibilityUpdate, { passive: true });

    this.#scheduleStickyVisibilityUpdate();
  }

  #scheduleStickyVisibilityUpdate = () => {
    if (this.#stickyVisibilityFrame) return;

    this.#stickyVisibilityFrame = requestAnimationFrame(() => {
      this.#stickyVisibilityFrame = null;
      this.#syncStickyVisibility();
    });
  };

  #syncStickyVisibility() {
    const trigger = this.#getStickyTrigger();
    const { stickyBar, stickyAddButton } = this.refs;

    if (!trigger || !stickyBar) return;

    const triggerRect = trigger.getBoundingClientRect();
    const topBoundary = this.#stickyTopBoundary();
    const triggerHasPassed = triggerRect.bottom <= topBoundary;
    const footerBlocking = this.#isFooterBlockingSticky(stickyBar);
    const canShow = this.#isAvailable()
      ? this.dataset.state === 'stepper' || stickyAddButton?.disabled === false
      : true;

    if (canShow && triggerHasPassed && !footerBlocking) {
      this.#showSticky();
    } else {
      this.#hideSticky();
    }
  }

  #getStickyTrigger() {
    return (
      this.refs.addButton?.closest('.dynamic-atc__control') ??
      this.querySelector('.dynamic-atc__control') ??
      this
    );
  }

  #stickyTopBoundary() {
    const header = document.querySelector('#header-component[sticky], header-component[sticky]');
    if (!(header instanceof HTMLElement)) return 0;

    return Math.max(0, header.getBoundingClientRect().height || 0);
  }

  #isFooterBlockingSticky(stickyBar) {
    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!(footer instanceof HTMLElement)) return false;

    const stickyHeight = stickyBar.offsetHeight || 0;
    const footerTop = footer.getBoundingClientRect().top;

    return footerTop <= window.innerHeight - stickyHeight - 16;
  }

  #showSticky() {
    const { stickyBar } = this.refs;
    if (stickyBar) {
      stickyBar.dataset.stuck = 'true';
      stickyBar.removeAttribute('aria-hidden');
    }
  }

  #hideSticky() {
    const { stickyBar } = this.refs;
    if (stickyBar) {
      stickyBar.dataset.stuck = 'false';
      stickyBar.setAttribute('aria-hidden', 'true');
    }
  }

  #bindStickyButtons() {
    this.#stickyButtonAbortController?.abort();
    this.#stickyButtonAbortController = new AbortController();
    const { signal } = this.#stickyButtonAbortController;
    const { stickyAddButton, stickyMinusBtn, stickyPlusBtn } = this.refs;
    stickyAddButton?.addEventListener('click', (event) => this.handleAdd(event, stickyAddButton), { signal });
    stickyMinusBtn?.addEventListener('click', () => this.handleMinus(), { signal });
    stickyPlusBtn?.addEventListener('click', (event) => this.handlePlus(event, stickyPlusBtn), { signal });
  }

  #getFlySource(event, fallback) {
    if (fallback instanceof Element) return fallback;
    if (event?.target instanceof Element) {
      return event.target.closest('button') ?? this.refs.addButton;
    }

    return this.refs.addButton;
  }

  #readCartItemCount() {
    const selectors = ['[ref="cartBubbleCount"]', '[ref="cartItemCount"]'];

    for (const selector of selectors) {
      const count = parseInt(document.querySelector(selector)?.textContent?.trim() ?? '', 10);
      if (!Number.isNaN(count)) return count;
    }

    try {
      const cachedCount = JSON.parse(sessionStorage.getItem('cart-count') || '{}').value;
      const count = parseInt(cachedCount, 10);
      if (!Number.isNaN(count)) return count;
    } catch (_) {
      // no-op
    }

    return 0;
  }

  #cartSectionIds() {
    const sections = new Set();

    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sections.add(component.dataset.sectionId);
      }
    });

    return Array.from(sections);
  }
}

if (!customElements.get('dynamic-atc-component')) {
  customElements.define('dynamic-atc-component', DynamicAtcComponent);
}
